import { useMemo, useState } from 'react';
import {
  usePreisToolFixedPrices,
  usePreisToolSuppliers,
  usePreisToolUpstreamSupplierNames,
  useRemoveFixedPrice,
  useRemoveUpstreamSupplier,
  useSaveFixedPrice,
  useSaveUpstreamSupplier,
} from '@/api/preis-tool/preis-tool.hooks';
import type {
  PreisToolFixedPrice,
  PreisToolItem,
} from '@/api/preis-tool/preis-tool.types';
import { DsAlert, DsButton, DsCombobox, DsInput, DsModal } from '@/modules/internal-v2/ds';
import {
  fmtDate,
  fmtEUR,
  newSupplierLabel,
  offerStockSummary,
  parsePriceInput,
  supplierGroups,
  UPSTREAM_SUPPLIERS_ENABLED,
  upstreamStockSummary,
} from '../helpers';

interface FixedPriceDialogProps {
  article: PreisToolItem;
  /** Without `PREIS_TOOL_MANAGE` the dialog only lists the fixed prices. */
  canEdit: boolean;
  /** Fixed price whose stock (Menge and Vorlieferanten) is unfolded on opening. */
  initialStockOpenId?: string;
  onClose: () => void;
}

interface FormState {
  supplierName: string;
  netPrice: string;
  validTo: string;
  uvpNet: string;
  uvpGross: string;
  quantity: string;
}

const EMPTY_FORM: FormState = {
  supplierName: '',
  netPrice: '',
  validTo: '',
  uvpNet: '',
  uvpGross: '',
  quantity: '',
};

/** A whole number of pieces, `null` for anything else; "1.200" is 1200. */
const parseQuantityInput = (text: string): number | null => {
  const cleaned = text.trim().replace(/[.\s]/g, '');
  return /^\d+$/.test(cleaned) ? Number(cleaned) : null;
};

const priceText = (value: number | null) =>
  value == null ? '' : value.toFixed(2).replace('.', ',');

/**
 * Fixed prices of one item (EOS-57): purchase prices a supplier has guaranteed
 * until a date. Lists the active and the expired ones and lets the user add,
 * change and remove them.
 */
export function FixedPriceDialog({
  article,
  canEdit,
  initialStockOpenId,
  onClose,
}: FixedPriceDialogProps) {
  const { data, isPending } = usePreisToolFixedPrices(article.id);
  const { data: supplierData } = usePreisToolSuppliers();
  const save = useSaveFixedPrice();
  const remove = useRemoveFixedPrice();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  // The fixed price whose upstream suppliers are unfolded.
  const [stockOpenId, setStockOpenId] = useState<string | null>(initialStockOpenId ?? null);
  // The fixed price whose removal waits for a second click.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const fixedPrices = data?.fixedPrices ?? [];
  const active = fixedPrices.filter((f) => !f.expired);
  const expired = fixedPrices.filter((f) => f.expired);
  const maxActive = data?.maxActive ?? 4;
  const suppliers = supplierData?.suppliers ?? [];
  const supplierOptions = useMemo(() => supplierGroups(supplierData), [supplierData]);

  const set =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // UVP gross follows UVP net with the item's VAT rate until the user types a
  // gross value of their own.
  const vatRate = data?.vatRate ?? null;
  const [grossTouched, setGrossTouched] = useState(false);
  const setUvpNet = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uvpNet = e.target.value;
    setForm((prev) => {
      if (grossTouched || vatRate == null) return { ...prev, uvpNet };
      const net = parsePriceInput(uvpNet);
      return {
        ...prev,
        uvpNet,
        uvpGross: net == null ? '' : priceText(Math.round(net * (100 + vatRate)) / 100),
      };
    });
  };

  const edit = (fixedPrice: PreisToolFixedPrice) => {
    setFormError(null);
    setGrossTouched(fixedPrice.uvpGross != null);
    setForm({
      supplierName: fixedPrice.supplierName,
      netPrice: priceText(fixedPrice.netPrice),
      validTo: fixedPrice.validTo,
      uvpNet: priceText(fixedPrice.uvpNet),
      uvpGross: priceText(fixedPrice.uvpGross),
      quantity: fixedPrice.quantity == null ? '' : String(fixedPrice.quantity),
    });
  };

  const submit = () => {
    const supplierName = form.supplierName.trim();
    const netPrice = parsePriceInput(form.netPrice);
    const uvpNet = parsePriceInput(form.uvpNet);
    const uvpGross = parsePriceInput(form.uvpGross);
    if (!supplierName) return setFormError('Bitte einen Lieferanten angeben.');
    if (netPrice == null || netPrice <= 0)
      return setFormError('Bitte einen festen EK netto größer 0 angeben.');
    if (!form.validTo) return setFormError('Bitte angeben, bis wann der Fixpreis gilt.');
    if (form.uvpNet.trim() && uvpNet == null)
      return setFormError('UVP netto ist keine Zahl.');
    if (form.uvpGross.trim() && uvpGross == null)
      return setFormError('UVP brutto ist keine Zahl.');
    const quantity = parseQuantityInput(form.quantity);
    if (form.quantity.trim() && quantity == null)
      return setFormError('Die Menge ist keine ganze Zahl.');
    setFormError(null);

    const known = suppliers.find(
      (s) => s.name.trim().toLowerCase() === supplierName.toLowerCase(),
    );
    save.mutate(
      {
        itemId: article.id,
        supplierName,
        supplierBpId: known?.id,
        netPrice,
        validTo: form.validTo,
        uvpNet: uvpNet ?? undefined,
        uvpGross: uvpGross ?? undefined,
        quantity: quantity ?? undefined,
      },
      {
        onSuccess: () => {
          setForm(EMPTY_FORM);
          setGrossTouched(false);
        },
      },
    );
  };

  const saveError = save.isError
    ? save.error.response?.status === 409
      ? `Es gibt bereits ${maxActive} aktive Fixpreise für diesen Artikel. Entferne einen, bevor du einen weiteren anlegst.`
      : 'Der Fixpreis konnte nicht gespeichert werden.'
    : null;

  const row = (fixedPrice: PreisToolFixedPrice) => {
    const stock = upstreamStockSummary(fixedPrice.upstreamSuppliers);
    const stockOpen = stockOpenId === fixedPrice.id;
    // Its own quantity plus the stock of its upstream suppliers, split up in the tooltip.
    const total = offerStockSummary(fixedPrice.quantity, fixedPrice.upstreamSuppliers);
    return (
      <div key={fixedPrice.id}>
        <div className={`pt-fix-row${fixedPrice.expired ? ' expired' : ''}`}>
          <span className="supplier">
            <span className="name" title={fixedPrice.supplierName}>
              {fixedPrice.supplierName}
            </span>
            {(UPSTREAM_SUPPLIERS_ENABLED || total.total != null || fixedPrice.origin === 'IMPORT') && (
              <span className="meta">
                {total.total != null ? (
                  <span title={total.title}>Σ {total.total.toLocaleString('de-DE')} Stk</span>
                ) : (
                  UPSTREAM_SUPPLIERS_ENABLED && <span title={stock.title}>{stock.text}</span>
                )}
                {fixedPrice.origin === 'IMPORT' && (
                  <span
                    className="ds-pill muted"
                    title="Aus einer hochgeladenen Preisliste übernommen"
                  >
                    Preisliste
                  </span>
                )}
              </span>
            )}
          </span>
          <span className="price">€ {fmtEUR(fixedPrice.netPrice)}</span>
          <span className="valid">
            {fixedPrice.expired ? (
              <span className="ds-pill neg" title={`gültig bis ${fmtDate(fixedPrice.validTo)}`}>
                abgelaufen
              </span>
            ) : (
              `bis ${fmtDate(fixedPrice.validTo)}`
            )}
          </span>
          {/* Hidden while a removal waits for confirmation, so the supplier
              name keeps its room. */}
          {UPSTREAM_SUPPLIERS_ENABLED && confirmRemoveId !== fixedPrice.id && (
            <span className="actions">
              <DsButton
                variant="ghost"
                size="small"
                onClick={() => setStockOpenId(stockOpen ? null : fixedPrice.id)}
                title="Vorlieferanten und ihren Bestand anzeigen"
              >
                {stockOpen ? 'Lager ▾' : 'Lager ▸'}
              </DsButton>
            </span>
          )}
          {canEdit && confirmRemoveId === fixedPrice.id && (
            <span className="actions">
              <span className="confirm">Wirklich entfernen?</span>
              <DsButton
                variant="primary"
                size="small"
                onClick={() =>
                  remove.mutate(fixedPrice.id, { onSuccess: () => setConfirmRemoveId(null) })
                }
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Entfernen…' : 'Entfernen'}
              </DsButton>
              <DsButton
                variant="ghost"
                size="small"
                onClick={() => setConfirmRemoveId(null)}
                disabled={remove.isPending}
              >
                Abbrechen
              </DsButton>
            </span>
          )}
          {canEdit && confirmRemoveId !== fixedPrice.id && (
            <span className="actions">
              <DsButton
                variant="ghost"
                size="small"
                onClick={() => edit(fixedPrice)}
                title="In das Formular übernehmen"
              >
                Ändern
              </DsButton>
              <DsButton
                variant="ghost"
                size="small"
                onClick={() => {
                  remove.reset();
                  setConfirmRemoveId(fixedPrice.id);
                }}
                title="Fixpreis entfernen"
              >
                Entfernen
              </DsButton>
            </span>
          )}
        </div>
        {UPSTREAM_SUPPLIERS_ENABLED && stockOpen && (
          <UpstreamSuppliers fixedPrice={fixedPrice} canEdit={canEdit} />
        )}
      </div>
    );
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title="Fixpreise"
      description={`${article.id} · ${article.name}`}
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose}>
            Schließen
          </DsButton>
          {canEdit && (
            <DsButton
              variant="primary"
              onClick={submit}
              disabled={save.isPending}
            >
              {save.isPending ? 'Speichern…' : 'Fixpreis speichern'}
            </DsButton>
          )}
        </>
      }
    >
      <div className="pt-form">
        <div className="pt-fix-list">
          <div className="pt-label">
            Aktive Fixpreise · {active.length} von {maxActive}
          </div>
          {isPending ? (
            <div className="pt-fix-empty">Lade…</div>
          ) : active.length === 0 ? (
            <div className="pt-fix-empty">
              Für diesen Artikel ist kein Fixpreis hinterlegt.
            </div>
          ) : (
            active.map(row)
          )}
          {expired.length > 0 && (
            <>
              <div className="pt-label" style={{ marginTop: 10 }}>
                Abgelaufen · {expired.length}
              </div>
              {expired.map(row)}
            </>
          )}
        </div>

        {remove.isError && (
          <DsAlert severity="error">
            Der Fixpreis konnte nicht entfernt werden.
          </DsAlert>
        )}

        {canEdit && (
          <>
            <div className="pt-label">Fixpreis anlegen oder ändern</div>
            {(formError ?? saveError) && (
              <DsAlert severity="error">{formError ?? saveError}</DsAlert>
            )}
            <label className="pt-field">
              <span className="pt-label">Lieferant</span>
              <DsCombobox
                value={form.supplierName}
                onChange={(supplierName) => setForm((prev) => ({ ...prev, supplierName }))}
                groups={supplierOptions}
                freeText={{ createLabel: newSupplierLabel }}
                placeholder="Name wählen oder frei eintippen"
                maxLength={200}
              />
            </label>
            <div className="pt-field-row">
              <label className="pt-field">
                <span className="pt-label">Fester EK netto (€)</span>
                <DsInput
                  mono
                  value={form.netPrice}
                  onChange={set('netPrice')}
                  placeholder="0,00"
                  inputMode="decimal"
                />
              </label>
              <label className="pt-field">
                <span className="pt-label">Gültig bis (einschließlich, Pflicht)</span>
                <DsInput
                  type="date"
                  value={form.validTo}
                  onChange={set('validTo')}
                />
              </label>
            </div>
            <div className="pt-field-row">
              <label className="pt-field">
                <span className="pt-label">UVP netto (€, optional)</span>
                <DsInput
                  mono
                  value={form.uvpNet}
                  onChange={setUvpNet}
                  placeholder="0,00"
                  inputMode="decimal"
                />
              </label>
              <label className="pt-field">
                <span className="pt-label">
                  UVP brutto (€, optional{vatRate != null ? `, aus netto + ${vatRate.toLocaleString('de-DE')} % MwSt.` : ''})
                </span>
                <DsInput
                  mono
                  value={form.uvpGross}
                  onChange={(e) => {
                    setGrossTouched(true);
                    set('uvpGross')(e);
                  }}
                  placeholder="0,00"
                  inputMode="decimal"
                />
              </label>
            </div>
            <div className="pt-field-row">
              <label className="pt-field">
                <span className="pt-label">Menge (Stück, optional)</span>
                <DsInput
                  mono
                  value={form.quantity}
                  onChange={set('quantity')}
                  placeholder="z. B. 80"
                  inputMode="numeric"
                />
              </label>
            </div>
            <div className="pt-fix-hint">
              Denselben Lieferanten erneut zu speichern ändert seinen
              bestehenden Fixpreis. Je Artikel sind höchstens {maxActive} aktive
              Fixpreise möglich.
            </div>
          </>
        )}
      </div>
    </DsModal>
  );
}

/**
 * Upstream suppliers of one fixed price (EOS-58): where the supplier gets the
 * goods from and how many pieces lie there. Entering a name again updates its
 * stock; an empty stock means "unknown".
 */
function UpstreamSuppliers({
  fixedPrice,
  canEdit,
}: {
  fixedPrice: PreisToolFixedPrice;
  canEdit: boolean;
}) {
  const { data: known } = usePreisToolUpstreamSupplierNames();
  const save = useSaveUpstreamSupplier();
  const remove = useRemoveUpstreamSupplier();
  const [name, setName] = useState('');
  const [stock, setStock] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const trimmed = name.trim();
    const stockText = stock.trim();
    if (!trimmed) return setError('Bitte einen Vorlieferanten angeben.');
    if (stockText !== '' && !/^\d+$/.test(stockText))
      return setError('Der Bestand muss eine ganze Zahl sein.');
    setError(null);
    save.mutate(
      {
        fixedPriceId: fixedPrice.id,
        body: {
          name: trimmed,
          stockQty: stockText === '' ? null : Number(stockText),
        },
      },
      {
        onSuccess: () => {
          setName('');
          setStock('');
        },
      },
    );
  };

  const upstreamOptions = useMemo(
    () => [{ options: (known?.names ?? []).map((n) => ({ value: n, label: n })) }],
    [known],
  );
  const summary = upstreamStockSummary(fixedPrice.upstreamSuppliers);

  return (
    <div className="pt-upstream">
      <div className="pt-label">
        Vorlieferanten / Lagerquellen · {fixedPrice.supplierName} ·{' '}
        {summary.text}
      </div>
      {fixedPrice.upstreamSuppliers.length === 0 ? (
        <div className="pt-fix-empty">Noch keine Lagerquelle hinterlegt.</div>
      ) : (
        fixedPrice.upstreamSuppliers.map((u) => (
          <div key={u.id} className="pt-upstream-row">
            <span className="name" title={u.name}>
              {u.name}
            </span>
            <span className="qty">
              {u.stockQty == null
                ? 'unbekannt'
                : `${u.stockQty.toLocaleString('de-DE')} Stk`}
            </span>
            {canEdit && (
              <DsButton
                variant="ghost"
                size="small"
                onClick={() =>
                  remove.mutate({
                    fixedPriceId: fixedPrice.id,
                    upstreamSupplierId: u.id,
                  })
                }
                disabled={remove.isPending}
              >
                Entfernen
              </DsButton>
            )}
          </div>
        ))
      )}
      {canEdit && (
        <>
          {(error || save.isError || remove.isError) && (
            <DsAlert severity="error">
              {error ?? 'Die Lagerquelle konnte nicht gespeichert werden.'}
            </DsAlert>
          )}
          <div className="pt-upstream-add">
            <label className="pt-field">
              <span className="pt-label">Vorlieferant</span>
              <DsCombobox
                value={name}
                onChange={setName}
                groups={upstreamOptions}
                freeText={{ createLabel: (n) => `„${n}“ als neuen Vorlieferanten verwenden` }}
                placeholder="Name wählen oder frei eintippen"
                maxLength={200}
              />
            </label>
            <label className="pt-field" style={{ flex: '0 0 130px' }}>
              <span className="pt-label">Bestand (Stk)</span>
              <DsInput
                mono
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') add();
                }}
                placeholder="leer = unbekannt"
                title="Leer lassen, wenn der Bestand nicht bekannt ist"
                inputMode="numeric"
              />
            </label>
            <DsButton onClick={add} disabled={save.isPending}>
              Hinzufügen
            </DsButton>
          </div>
        </>
      )}
    </div>
  );
}
